import { useMemo } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { springs } from "@/components/ui/motion";
import type { WizardStep, ChecklistSource } from "./types";

type StepDef = { key: WizardStep; label: string };

const ORG_CONTEXT_STEP: StepDef = { key: "orgContext", label: "Context" };

const UPLOAD_STEPS_BASE: StepDef[] = [
  { key: "details", label: "Details" },
  { key: "profiling", label: "Depth" },
  { key: "extracting", label: "Extracting" },
  { key: "clauses", label: "Clauses" },
  { key: "generating", label: "Generating" },
  { key: "review", label: "Review" },
];

const UPLOAD_STEPS_WITH_PII: StepDef[] = [
  { key: "details", label: "Details" },
  { key: "piiRole", label: "PII Role" },
  { key: "profiling", label: "Depth" },
  { key: "extracting", label: "Extracting" },
  { key: "clauses", label: "Clauses" },
  { key: "generating", label: "Generating" },
  { key: "review", label: "Review" },
];

const DEFAULT_STEPS_BASE: StepDef[] = [
  { key: "details", label: "Details" },
  { key: "profiling", label: "Depth" },
  { key: "generating", label: "Generating" },
  { key: "review", label: "Review" },
];

const DEFAULT_STEPS_WITH_PII: StepDef[] = [
  { key: "details", label: "Details" },
  { key: "piiRole", label: "PII Role" },
  { key: "profiling", label: "Depth" },
  { key: "generating", label: "Generating" },
  { key: "review", label: "Review" },
];

function insertAfterProfiling(steps: StepDef[]): StepDef[] {
  const idx = steps.findIndex((s) => s.key === "profiling");
  const result = [...steps];
  result.splice(idx + 1, 0, ORG_CONTEXT_STEP);
  return result;
}

interface WizardStepIndicatorProps {
  currentStep: WizardStep;
  checklistSource?: ChecklistSource | null;
  hasRoleSelection?: boolean;
  hasOrgContext?: boolean;
}

export function WizardStepIndicator({
  currentStep,
  checklistSource,
  hasRoleSelection,
  hasOrgContext,
}: WizardStepIndicatorProps) {
  const isUpload = checklistSource === "upload";
  const baseSteps = isUpload
    ? hasRoleSelection
      ? UPLOAD_STEPS_WITH_PII
      : UPLOAD_STEPS_BASE
    : hasRoleSelection
      ? DEFAULT_STEPS_WITH_PII
      : DEFAULT_STEPS_BASE;
  const steps = useMemo(
    () => (hasOrgContext ? insertAfterProfiling(baseSteps) : baseSteps),
    [hasOrgContext, baseSteps],
  );
  const currentIndex = steps.findIndex((s) => s.key === currentStep);
  const progressPercent =
    steps.length <= 1
      ? 100
      : (Math.max(0, currentIndex) / (steps.length - 1)) * 100;

  return (
    <div className="pt-2 pb-1">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isActive = index === currentIndex;

          return (
            <div key={step.key} className="flex items-center gap-2">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                  isCompleted
                    ? "bg-accent text-white"
                    : isActive
                      ? "border-2 border-accent text-accent"
                      : "border-2 border-border text-muted"
                }`}
              >
                {isCompleted ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={springs.bouncy}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </motion.div>
                ) : (
                  index + 1
                )}
              </div>

              <span
                className={`text-[12px] font-medium ${
                  isActive
                    ? "text-accent"
                    : isCompleted
                      ? "text-primary"
                      : "text-muted"
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-3 h-[3px] rounded-full bg-border/50">
        <motion.div
          className="h-full rounded-full bg-accent"
          initial={{ width: "0%" }}
          animate={{ width: `${progressPercent}%` }}
          transition={springs.smooth}
        />
      </div>
    </div>
  );
}
